// Holding a reaction counter with the mouse (#714; web and Mac only, as the phones send jumbo from the picker): hold a
// counter 1.5 s or more and it grows, and on release the same emoji is sent as my jumbo reaction. Under 1.5 s it is the
// click it always was (a tap adds or takes back my reaction). A hold that sent or splatted swallows the click.
import { useEffect, useMemo, useRef } from "react";
import { HoldDriver, browserFlags } from "../jumboHold.ts";
import { getReactionEffects } from "../reactionEffects.ts";
import { playSplat, primeJumboSounds } from "../jumboSound.ts";
import { HoldOverlay } from "./HoldOverlay.tsx";

const SWALLOW_CLICK_MS = 100;

export interface ChipHold {
  down: (e: React.PointerEvent<HTMLElement>, messageId: string, emoji: string) => void;
  up: () => void;
  leave: () => void;
  cancel: () => void;
  /** True once after a hold that sent or splatted: the click that follows it is not a toggle. */
  swallowClick: () => boolean;
}

export function useChipHold(sendJumbo: (messageId: string, emoji: string) => void): { hold: ChipHold; overlay: React.ReactNode } {
  const latest = useRef(sendJumbo);
  latest.current = sendJumbo;
  const swallow = useRef(false);
  const holding = useRef<{ messageId: string; emoji: string } | null>(null);
  const driver = useMemo(
    () =>
      HoldDriver.browser({
        flags: browserFlags(getReactionEffects),
        onSplat: playSplat,
        onSend: (_target, send) => {
          // "super" and "normal" are clicks here: a counter has no super hold.
          if (send === "jumbo" && holding.current) latest.current(holding.current.messageId, holding.current.emoji);
        },
      }),
    [],
  );
  useEffect(() => () => driver.cancel(), [driver]);
  const hold = useMemo<ChipHold>(
    () => ({
      down: (e, messageId, emoji) => {
        if (e.pointerType !== "mouse" || e.button !== 0) return;
        swallow.current = false;
        primeJumboSounds();
        holding.current = { messageId, emoji };
        driver.press({ el: e.currentTarget, emoji });
      },
      up: () => {
        const was = driver.release();
        if (was !== "jumbo" && was !== "splat") return;
        swallow.current = true;
        setTimeout(() => (swallow.current = false), SWALLOW_CLICK_MS);
      },
      leave: () => driver.leave(),
      cancel: () => driver.cancel(),
      swallowClick: () => {
        const was = swallow.current;
        swallow.current = false;
        return was;
      },
    }),
    [driver],
  );
  return { hold, overlay: <HoldOverlay driver={driver} /> };
}
